<template>
  <v-card
    class="pa-0 h-100 d-flex flex-column"
    elevation="0"
    rounded="0"
    style="border-right: 1px #cbcbcb solid; overflow-x: hidden"
  >
    <v-card class="pa-0" elevation="2" rounded="0">
      <v-card-actions class="border-b-thin py-0 px-3">
        <v-icon icon="mdi-pin" />
        <div class="text-h5 pl-3" style="font-weight: 600">
          Services Directory <BetaChip class="ml-2" />
        </div>
      </v-card-actions>

      <v-card-actions class="py-0 px-3">
        <v-text-field
          v-model="locationSearch"
          placeholder="Search for a location..."
          variant="outlined"
          density="compact"
          rounded="0"
          clearable
          hide-details
          class="flex-grow-1 mt-3 mb-2"
          @keyup.enter="searchLocation"
          @click:clear="clearLocationSearch"
        >
          <template v-slot:append-inner>
            <v-btn
              icon="mdi-map-search-outline"
              variant="text"
              size="medium"
              @click="searchLocation"
              :loading="searchingLocation"
              color="primary"
              :ripple="false"
            />
          </template>
        </v-text-field>
      </v-card-actions>

      <v-tabs v-model="tab" color="#0E5B99" grow stacked>
        <v-tab value="areas" aria-label="Areas">
          <div class="position-relative">
            <v-icon icon="mdi-vector-polygon" size="22" class="mb-1" />
            <v-chip
              v-if="selectedCustomAreas.length"
              color="red"
              variant="flat"
              class="position-absolute tab-chip"
              size="x-small"
              density="compact"
            >
              {{ selectedCustomAreas.length }}
            </v-chip>
          </div>
          <div class="text-subtitle-2">Custom Areas</div>
        </v-tab>
        <v-tab value="services" aria-label="Services">
          <div class="position-relative">
            <v-icon icon="mdi-map-marker" size="22" class="mb-1 text-center" />
            <v-chip
              v-if="
                Object.keys(
                  this.$store.state.servicesDirectory.SDselectedServices,
                ).length > 0 && !fetchingServices
              "
              color="red"
              variant="flat"
              class="position-absolute tab-chip"
              size="x-small"
              density="compact"
            >
              {{ this.$store.state.servicesDirectory.SDcounts.totalServices }}
            </v-chip>
            <v-progress-circular
              v-if="fetchingServices"
              indeterminate
              class="position-absolute"
              style="
                top: -10px;
                right: -10px;
                font-size: 13px;
                height: 21px;
                min-width: 21px;
                padding: 0 5px;
              "
              color="primary"
              size="16"
              width="2"
            ></v-progress-circular>
          </div>
          <div class="text-subtitle-2">Services</div>
        </v-tab>
      </v-tabs>
    </v-card>

    <v-card-actions class="pa-0 flex-wrap">
      <div class="flex-1-1-100 px-4 mt-4 text-body-2 text-left">
        <span v-if="tab === 'areas'"
          >Select custom areas to appear on the map</span
        >
        <span v-else>Select services to explore</span>
      </div>
      <v-text-field
        v-model="searchString"
        :placeholder="
          tab === 'areas' ? 'Search custom areas' : 'Search services'
        "
        prepend-inner-icon="mdi-magnify"
        color="primary"
        variant="outlined"
        density="compact"
        clearable
        class="px-4 pt-2 pb-4"
        rounded="0"
        hide-details
        autocomplete="off"
      />
    </v-card-actions>
    <v-card-actions
      v-if="
        this.$store.state.servicesDirectory.SDcounts.totalServices === 0 &&
        Object.keys(this.$store.state.servicesDirectory.SDselectedServices)
          .length &&
        !fetchingServices
      "
    >
      <v-alert
        color="#FDD9AF"
        density="compact"
        rounded="lg"
        class="ml-2 mr-2 text-left"
        style="color: #8b4c04"
      >
        <div class="text-subtitle-2">No services found</div>
        <div class="text-body-2">
          Please try a different combination of area(s) and service(s) to
          explore.
        </div></v-alert
      >
    </v-card-actions>
    <v-card-actions v-if="disableServicesSelection">
      <v-alert
        color="#FDD9AF"
        density="compact"
        rounded="lg"
        class="ml-2 mr-2 text-left"
        style="color: #8b4c04"
      >
        <div class="text-subtitle-2">Too many services selected</div>
        <div class="text-body-2">
          You've selected more services than can be displayed on the map. To
          keep things running smoothly, please narrow down your selection.
        </div>
      </v-alert>
    </v-card-actions>

    <v-card
      class="flex-grow-1 border-b-thin overflow-y-auto"
      style="height: 10px"
      elevation="0"
    >
      <v-tabs-window v-model="tab">
        <v-tabs-window-item value="areas">
          <v-list
            class="ma-0 pa-0"
            density="compact"
            rounded="0"
            id="customAreaCategoriesList"
            style="z-index: 900"
            :opened="opened"
          >
            <v-card-text
              v-if="searchString && !searchHits"
              class="text-center pt-4 px-8 py-6"
            >
              <!-- No  matches your search term -->
              <div class="pb-3">
                <v-icon icon="mdi-emoticon-sad-outline" size="32" />
              </div>
              <div><strong>No custom areas found</strong></div>
              <div>
                We couldn't find any custom areas that matched your search term
              </div>
            </v-card-text>
            <div v-for="category in customAreas" :key="category.id">
              <v-list-group
                no-action
                :value="category.id"
                v-model="category.expanded"
                v-if="!category.hidden"
                density="compact"
                rounded="0"
              >
                <template v-slot:activator="{ props }">
                  <v-list-item
                    v-bind="props"
                    v-model="category.expanded"
                    :value="category.expanded"
                    class="px-3 py-1"
                    density="compact"
                    tabindex="0"
                    rounded="0"
                  >
                    <v-list-item-action>
                      <v-layout style="display: flex; align-items: center">
                        <v-checkbox
                          v-model="category.checked"
                          @click.stop="toggleCustomAreaCategory(category)"
                          density="compact"
                          :aria-label="`Select all areas in ${category.name}`"
                          :aria-checked="category.checked ? 'true' : 'false'"
                          color="primary"
                          :indeterminate="isCategoryIndeterminate(category)"
                          tabindex="0"
                          rounded="0"
                        ></v-checkbox>
                        <span class="text-left w-100 pl-1">{{
                          category.name
                        }}</span></v-layout
                      >
                    </v-list-item-action>
                  </v-list-item>
                </template>

                <template v-if="category.customAreas.length">
                  <template
                    v-for="(area, i) in category.customAreas"
                    :key="`area-${i}`"
                  >
                    <v-list-item
                      v-if="!area.hidden"
                      density="compact"
                      rounded="0"
                      tabindex="0"
                      @click.stop="toggleCustomArea(area, category)"
                    >
                      <v-list-item-action>
                        <v-layout style="display: flex; align-items: center">
                          <v-checkbox
                            v-model="area.checked"
                            density="compact"
                            color="primary"
                            :aria-label="`Select area ${area.name}`"
                            :aria-checked="area.checked ? 'true' : 'false'"
                            class="mx-2"
                            tabindex="0"
                            rounded="0"
                          ></v-checkbox>
                          <span class="text-left w-100 pl-1">{{
                            area.name
                          }}</span>
                        </v-layout>
                      </v-list-item-action>
                    </v-list-item>
                  </template>
                </template>
                <template v-else>
                  <v-list-item density="compact" rounded="0">
                    <v-list-item-action>
                      <span>No areas in this category</span>
                    </v-list-item-action>
                  </v-list-item>
                </template>
              </v-list-group>
            </div>
          </v-list>
        </v-tabs-window-item>

        <v-tabs-window-item value="services" :eager="true">
          <ServicesDirectoryList
            :search-string="searchString"
            @getServices="handleServicesChange"
            :disableServicesSelection="disableServicesSelection"
            :service-counts="
              this.$store.state.servicesDirectory.SDcounts?.serviceCounts || {}
            "
          />
        </v-tabs-window-item>
      </v-tabs-window>
    </v-card>
  </v-card>
</template>

<script>
/* global google */
import BetaChip from "@/components/BetaChip.vue";
import { systemMessages } from "@/mixins/SystemMessages";
import ServicesDirectoryList from "@/components/ServicesDirectoryList";

export default {
  name: "ServicesDirectorySidebar",
  mixins: [systemMessages],
  components: {
    BetaChip,
    ServicesDirectoryList,
  },
  data: () => ({
    customAreas: [],
    searchHits: 0,
    opened: [],
    debounceTimer: null,
    currentRequest: null, // Store the abort controller
    hasPendingChanges: false, // Track if there are changes waiting to be processed
    locationSearch: "",
    searchingLocation: false,
  }),
  mounted() {
    this.getAllAreas();

    this.selectedCustomAreas = this.$route.query?.selectedCustomAreas
      ? JSON.parse(this.$route.query.selectedCustomAreas)
      : [];
  },
  computed: {
    disableServicesSelection() {
      if (!this.$store.state.servicesDirectory.SDcounts?.totalServices) {
        return false;
      }
      return (
        this.$store.state.servicesDirectory.SDcounts.totalServices > 100000
      );
    },
    isProcessing() {
      return this.fetchingCustomAreas;
    },
    fetchingCustomAreas: {
      get() {
        return this.$store.getters["servicesDirectory/fetchingCustomAreas"];
      },
      set(val) {
        this.$store.commit("servicesDirectory/setFetchingCustomAreas", val);
      },
    },
    fetchedCustomAreaGeometries: {
      get() {
        return this.$store.getters[
          "servicesDirectory/fetchedCustomAreaGeometries"
        ];
      },
      set(val) {
        this.$store.commit(
          "servicesDirectory/setFetchedCustomAreaGeometries",
          val,
        );
      },
    },
    selectedCustomAreas: {
      get() {
        return this.$store.getters["servicesDirectory/selectedCustomAreas"];
      },
      set(val) {
        // the value is synced with URL as well as with vuex
        this.$router.push({
          query: {
            ...this.$route.query,
            selectedCustomAreas: JSON.stringify(val),
          },
        });
        this.$store.commit("servicesDirectory/setSelectedCustomAreas", val);
      },
    },
    fetchingServices: {
      get() {
        return this.$store.getters["servicesDirectory/fetchingServices"];
      },
      set(val) {
        this.$store.commit("servicesDirectory/setFetchingServices", val);
      },
    },
    selectedServices: {
      get() {
        return this.$store.getters["servicesDirectory/SDselectedServices"];
      },
      set(val) {
        this.$store.commit("servicesDirectory/setSDselectedServices", val);
      },
    },
    servicesGroupsAndMetaHierarchy: {
      get() {
        return this.$store.getters[
          "servicesDirectory/servicesGroupsAndMetaHierarchy"
        ];
      },
    },
    selectedURLServiceIds: {
      get() {
        return this.$store.getters["servicesDirectory/selectedURLServiceIds"];
      },
      set(val) {
        this.$store.commit("servicesDirectory/setSelectedURLServiceIds", val);
      },
    },
    /**
     * The properties are synced with the URL
     */
    tab: {
      get() {
        return this.$route.query?.tab ?? "areas";
      },
      set(val) {
        this.$router.push({
          query: { ...this.$route.query, tab: val },
        });
      },
    },
    areasSearch: {
      get() {
        return this.$route.query?.areasSearch;
      },
      set(val) {
        this.$router.push({
          query: { ...this.$route.query, areasSearch: val },
        });
      },
    },
    servicesSearch: {
      get() {
        return this.$route.query?.servicesSearch;
      },
      set(val) {
        this.$router.push({
          query: { ...this.$route.query, servicesSearch: val },
        });
      },
    },
    /*
        This property dynamically determines which search term to use
        based on the current tab. It allows for a single v-model binding for the
        search input.
    */
    searchString: {
      get() {
        return this[this.tab + "Search"] ?? "";
      },
      set(val) {
        this[this.tab + "Search"] = val;
      },
    },
  },
  methods: {
    async buildSVGs() {
      const iconPromises = Object.entries(
        this.$store.state.servicesDirectory.SDmetadataIcons,
      ).map(async ([type_id, icon]) => {
        if (
          typeof this.$store.state.servicesDirectory.SDbuiltPNGIcons[type_id] ==
          "undefined"
        ) {
          let svgContent =
            '<svg xmlns="http://www.w3.org/2000/svg" height="25px" width="25px"><path d="' +
            icon.svg_path +
            '" fill="' +
            icon.colour +
            '"/></svg>';

          // convert the svg to a png
          const blob = await this.svgToPng(svgContent, 38, 38);
          const iconUrl = URL.createObjectURL(blob);

          return { type_id, iconUrl };
        }
        return null;
      });

      // Wait for all PNGs to be built
      const results = await Promise.all(iconPromises);

      // Filter out null results and create a single object with all icons
      const newIcons = results.reduce((acc, result) => {
        if (result) {
          acc[result.type_id] = result.iconUrl;
        }
        return acc;
      }, {});

      // Commit all new icons at once
      if (Object.keys(newIcons).length > 0) {
        this.$store.commit("servicesDirectory/setSDbuiltPNGIcons", {
          ...this.$store.state.servicesDirectory.SDbuiltPNGIcons,
          ...newIcons,
        });
      }

      // Emit the message after all PNGs are built
      this.$emit("servicesLoaded");
    },
    svgToPng(svgContent, width, height) {
      return new Promise((resolve, reject) => {
        const svgBlob = new Blob([svgContent], {
          type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            URL.revokeObjectURL(url);
            resolve(blob);
          }, "image/png");
        };
        img.onerror = (err) => {
          URL.revokeObjectURL(url);
          reject(err);
        };
        img.src = url;
      });
    },
    handleServicesChange() {
      // Cancel any existing request
      if (this.currentRequest) {
        this.currentRequest.abort();
        this.currentRequest = null;
      }

      // clear any fetched data and clear the map markers
      this.$store.commit("servicesDirectory/setSDcounts", {
        totalServices: 0,
        serviceCounts: {},
      });
      this.$store.commit("servicesDirectory/setSDmetadataIcons", {});
      this.$store.commit("servicesDirectory/setSDfetchedServices", {});
      this.$emit("clearMarkers");

      // Check if there are any services selected
      if (this.$store.state.servicesDirectory.SDselectedServices.length > 0) {
        // Show spinner immediately (and keep it on if already running)
        this.fetchingServices = true;

        // Clear existing timer and set a new one (extends the debounce)
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debouncedFetchServices();
      } else {
        // Clear any pending changes if no services are selected
        this.hasPendingChanges = false;
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = null;
        }
        // Only hide spinner if no services are selected
        this.fetchingServices = false;
      }
    },
    debouncedFetchServices() {
      // Set new timer
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null;

        // Check if there are still services selected before making the API call
        if (this.$store.state.servicesDirectory.SDselectedServices.length > 0) {
          this.fetchSelectedServices();
        } else {
          // No services selected, just hide the spinner
          this.fetchingServices = false;
        }
      }, 2000); // 2 second debounce
    },
    async fetchSelectedServices() {
      // Cancel any existing request
      if (this.currentRequest) {
        this.currentRequest.abort();
      }

      // Create new abort controller
      this.currentRequest = new AbortController();

      const selectedServices =
        this.$store.state.servicesDirectory.SDselectedServices.map(
          (service) => service.id,
        ).join(",");
      const selectedAreas =
        this.$store.state.servicesDirectory.selectedCustomAreas;

      try {
        const response = await this.$axios.post(
          "/sd-services/selected",
          {
            sdIds: selectedServices.split(",").filter((id) => id),
            areaIds: selectedAreas,
          },
          {
            signal: this.currentRequest.signal,
          },
        );

        // Handle successful response
        this.$store.commit(
          "servicesDirectory/setSDfetchedServices",
          response.data.services,
        );
        this.$store.commit(
          "servicesDirectory/setSDcounts",
          response.data.counts,
        );
        this.$store.commit(
          "servicesDirectory/setSDmetadataIcons",
          response.data.metadataIcons,
        );
        this.buildSVGs();
        //send analytics event for areas and services selected
        this.$axios.put("/send-analytics", {
          event: "services_directory_services_selected",
          areas: this.selectedCustomAreas.length, // Count of selected areas
          services: this.selectedServices // comma-separated list of selected service names
            .map((service) => service.service)
            .join(","),
          servicesGroup: this.servicesGroupsAndMetaHierarchy //comma-separated list of selected service groups
            .filter(
              (group) =>
                Array.isArray(group.metadata) &&
                group.metadata.some((meta) => meta.selected === true),
            )
            .map((group) => group.name)
            .join(","),
        });
      } catch (error) {
        if (error.name === "AbortError") {
          // Don't hide spinner here - let the debounce timer control it
          return;
        } else {
          console.error("Error fetching selected services:", error);
        }
      } finally {
        // Only hide spinner if there's no pending debounce timer
        if (!this.debounceTimer) {
          this.fetchingServices = false;
        }
        this.currentRequest = null;
      }
    },
    async getAllAreas() {
      this.fetchingCustomAreas = true;
      try {
        const response = await this.$axios.get(
          "/custom-areas-by-category-complete",
        );
        this.customAreas = response.data;
        this.searchCustomAreas(this.customAreas);

        // update checkboxes
        this.customAreas.forEach((category) => {
          category.customAreas.forEach((area) => {
            area.checked = this.selectedCustomAreas.includes(area.id);
          });
          category.checked =
            category.customAreas.length > 0 &&
            category.customAreas.every((a) => a.checked);
        });
        // Fetch services after areas are loaded if there are any services selected
        if (this.$store.state.servicesDirectory.SDselectedServices.length > 0) {
          this.handleServicesChange();
        }
      } catch (error) {
        this.errorMsg({
          title: "Error fetching areas",
          message: error?.response?.data?.message,
          timeout: 6000,
        });
      } finally {
        this.fetchingCustomAreas = false;
      }
    },
    toggleCustomArea(area, category) {
      area.checked = !area.checked;
      this.$nextTick().then(() => {
        const prev = this.selectedCustomAreas || [];
        let next = [];
        if (area.checked) {
          next = [...prev, area.id];
          if (category.customAreas.every((i) => i.checked)) {
            category.checked = true;
          }
        } else {
          next = prev.filter((i) => i !== area.id);
          if (category.checked) {
            category.checked = false;
          }
        }
        // ← assignment triggers computed.set()
        this.selectedCustomAreas = next;

        // now go and get the services
        this.handleServicesChange();
      });
    },
    toggleCustomAreaCategory(selCat) {
      selCat.checked = !selCat.checked;
      this.$nextTick().then(() => {
        const prev = this.selectedCustomAreas || [];
        let next = [];
        if (selCat.checked) {
          next = [
            ...prev,
            ...selCat.customAreas
              .map((a) => a.id)
              .filter((id) => !prev.includes(id)),
          ];
        } else {
          next = prev.filter(
            (id) => !selCat.customAreas.some((a) => a.id === id),
          );
        }
        this.selectedCustomAreas = next;

        // Update the visibility of custom areas based on the selection
        for (let i = 0; i < selCat.customAreas.length; i++) {
          selCat.customAreas[i].checked = selCat.checked;
        }
        // Only fetch services if there are any services selected
        if (this.$store.state.servicesDirectory.SDselectedServices.length > 0) {
          this.handleServicesChange();
        }
      });
    },
    searchCustomAreas() {
      const categories = this.customAreas;

      if (categories?.length && this.searchString) {
        const search = this.searchString.toLowerCase().trim();

        // For each category
        for (let i = 0; i < categories.length; i++) {
          // Track whether to hide category and its custom areas
          var allAreasUnchecked = true;
          // For each custom area, set a hidden flag if it doesn't match search
          for (let j = 0; j < categories[i].customAreas.length; j++) {
            if (
              categories[i].customAreas[j].name.toLowerCase().indexOf(search) ==
              -1
            ) {
              categories[i].customAreas[j].hidden = true;
            } else {
              categories[i].customAreas[j].hidden = false;
              allAreasUnchecked = false;
            }
          }
          // Hide category
          if (
            allAreasUnchecked &&
            !categories[i].name.toLowerCase().includes(search)
          ) {
            categories[i].hidden = true;
          } else {
            categories[i].hidden = false;
            categories[i].expanded = true;
            // manually add the category id to the opened array if not there
            if (!this.opened.includes(categories[i].id)) {
              this.opened.push(categories[i].id);
            }
          }
          this.searchHits = categories.filter((cac) => !cac.hidden).length;
        }
      } else {
        // Reset visibility when there's nothing in the filter
        for (let i = 0; i < categories.length; i++) {
          for (let j = 0; j < categories[i].customAreas.length; j++) {
            categories[i].customAreas[j].hidden = false;
          }
          categories[i].hidden = false;
        }
        this.searchHits = 0;

        this.opened = [];
      }
    },
    isCategoryIndeterminate(category) {
      // Check if any area in the category is checked but not all
      return (
        category.customAreas.some((area) => area.checked === true) &&
        !category.customAreas.every((area) => area.checked === true)
      );
    },
    async searchLocation() {
      if (!this.locationSearch.trim()) return;

      this.searchingLocation = true;
      try {
        // Use Google Maps Geocoding service to search for the location
        const { Geocoder } = await google.maps.importLibrary("geocoding");
        const geocoder = new Geocoder();
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode(
            { address: this.locationSearch },
            (results, status) => {
              if (
                status === google.maps.GeocoderStatus.OK &&
                results.length > 0
              ) {
                resolve(results[0]);
              } else {
                reject(new Error(`Geocoding failed: ${status}`));
              }
            },
          );
        });

        // Emit the search result to the parent component
        this.$emit("locationSearch", {
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng(),
          address: result.formatted_address,
          bounds: result.geometry.viewport,
        });

        // Clear the search field
        this.locationSearch = "";
      } catch (error) {
        console.error("Location search failed:", error);
        this.errorMsg({
          title: "Location Search Failed",
          message:
            "Could not find the specified location. Please try a different search term.",
          timeout: 4000,
        });
      } finally {
        this.searchingLocation = false;
      }
    },
    clearLocationSearch() {
      this.locationSearch = "";
      // Emit clear event to reset any map state if needed
      this.$emit("locationSearchClear");
    },
    /**
     * At initial page load if there are services passed in the URL then
     * set SDselectedServices in Vuex with the full service objects so that
     * we can access service metadata for analytics
     */
    initialServicesLoadFromURL() {
      // Extract raw ID values from array of { id: number } objects
      const selectedIdsFromURL = this.selectedURLServiceIds.map(
        (obj) => obj.id,
      );

      // Flatten all metadata from all groups into a single array
      const allServiceMetadata = this.servicesGroupsAndMetaHierarchy.flatMap(
        (group) => group.metadata,
      );

      // Filter full objects that match the IDs in the URL
      const selectedServicesFromURL = allServiceMetadata.filter((service) =>
        selectedIdsFromURL.includes(service.id),
      );
      // Set the selected services in Vuex
      this.$store.commit(
        "servicesDirectory/setSDselectedServices",
        selectedServicesFromURL,
      );

      this.selectedURLServiceIds = []; // Clear the URL param after using it
    },
  },
  watch: {
    areasSearch() {
      this.searchCustomAreas(this.customAreas);
    },
    servicesGroupsAndMetaHierarchy: {
      handler(newVal) {
        // triggered when selectedURLServiceIds is populated at page load to set selectedServices
        if (
          Array.isArray(newVal) &&
          newVal.length > 0 &&
          this.selectedServices.length === 0 &&
          this.selectedURLServiceIds.length > 0
        ) {
          // If there are services in the URL set them properly in vuex from service object data
          this.initialServicesLoadFromURL();
        }
      },
    },
  },
};
</script>

<style scoped>
.tab-chip {
  top: -10px;
  left: 16px;
  font-size: 13px;
  height: 21px;
  min-width: fit-content;
  padding: 0 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

/* Make tabs equal width */
.v-tabs .v-tab {
  flex: 1 1 0;
  min-width: 0;
}

.v-tabs .v-tab__content {
  width: 100%;
  justify-content: center;
}

/*  Make the Minus-box checkbox primary colour  */
.sd-category-checkbox-indeterminate .v-selection-control__input > .v-icon {
  opacity: 1;
}
.sd-category-checkbox-indeterminate i {
  color: #1867c0;
}
</style>
